import { useState } from 'react';
import { MessageCircle, Mail, Instagram, MapPin, Clock, Send } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { siteConfig, waLink } from '../config/site';
import { Reveal } from '../components/Reveal';

const inputCls =
  'w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 focus:border-gold focus:outline-none transition-colors duration-300';
const labelCls = 'mb-2 block text-[11px] font-semibold uppercase tracking-[0.25em] text-navy/60';

const Contact = () => {
  const { lang, t } = useLanguage();
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');

  const handleSend = (e) => {
    e.preventDefault();
    const text = lang === 'id'
      ? `Halo YOELI CHAN JOURNEY! Saya ${name || '(nama belum diisi)'}.\n\n${message}`
      : `Hello YOELI CHAN JOURNEY! My name is ${name || '(not provided)'}.\n\n${message}`;
    window.open(waLink(text), '_blank', 'noopener,noreferrer');
  };

  const items = [
    { icon: MessageCircle, label: t.contact.whatsappLabel, value: siteConfig.whatsappDisplay, href: waLink(t.whatsappDefault), testid: 'contact-whatsapp' },
    { icon: Mail, label: t.contact.emailLabel, value: siteConfig.email, href: `mailto:${siteConfig.email}`, testid: 'contact-email' },
    { icon: Instagram, label: t.contact.instagramLabel, value: siteConfig.instagramHandle, href: siteConfig.instagramUrl, testid: 'contact-instagram' },
    { icon: MapPin, label: t.contact.officeLabel, value: siteConfig.officeLocation[lang], testid: 'contact-office' },
    { icon: Clock, label: t.contact.hours, value: t.contact.hoursValue, testid: 'contact-hours' },
  ];

  return (
    <div className="bg-ivory" data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-5 pb-28 pt-36 md:px-8 md:pt-44">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.contact.kicker}</p>
          <h1 className="mt-4 max-w-3xl font-heading text-4xl font-medium tracking-tight text-navy md:text-6xl">{t.contact.title}</h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/60">{t.contact.sub}</p>
        </Reveal>

        <div className="mt-16 grid gap-16 lg:grid-cols-2">
          <div className="flex flex-col" data-testid="contact-info">
            {items.map((item, i) => {
              const Icon = item.icon;
              const inner = (
                <>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/50 text-gold-dark transition-colors duration-300 group-hover:bg-gold group-hover:text-navy">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-navy/45">{item.label}</span>
                    <span className="mt-1 block text-base text-navy">{item.value}</span>
                  </span>
                </>
              );
              return (
                <Reveal key={i} delay={i * 0.06}>
                  {item.href ? (
                    <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                      data-testid={item.testid}
                      className="group flex items-center gap-5 border-b border-navy/10 py-6 transition-colors duration-300 hover:border-gold/50">
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={item.testid} className="group flex items-center gap-5 border-b border-navy/10 py-6">
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.15}>
            <form onSubmit={handleSend} className="border border-navy/10 bg-beige/50 p-6 md:p-10" data-testid="contact-form">
              <h2 className="font-heading text-2xl text-navy">{t.contact.formTitle}</h2>
              <div className="mt-8 flex flex-col gap-6">
                <div>
                  <label htmlFor="contact-name" className={labelCls}>{t.contact.name}</label>
                  <input id="contact-name" data-testid="contact-form-name" className={inputCls} placeholder={t.contact.namePh}
                    value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="contact-message" className={labelCls}>{t.contact.message}</label>
                  <textarea id="contact-message" rows={5} data-testid="contact-form-message" className={`${inputCls} resize-none`}
                    placeholder={t.contact.messagePh} value={message} onChange={(e) => setMessage(e.target.value)} />
                </div>
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  className="group inline-flex w-fit items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold-light"
                >
                  {t.contact.send}
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
